import { Box, CircularProgress, createTheme, ThemeProvider } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ethers } from 'ethers'
import { createContext, useEffect, useState } from 'react'
import { Route, Routes, useNavigate } from 'react-router-dom'
import ReconnectingWebSocket from 'reconnecting-websocket'
import { useAnalytics } from './analyticsService'
import './App.css'
import { Header } from './components/Header'
import { Navigation } from './components/Navigation'
import WalletContext from './context/WalletContext'
import { IApplicationContext, Identity } from './interfaces/applicationContext'
import { User } from './interfaces/user'
import {
    clearStorage,
    getAccessToken,
    isLoggedIn,
    parseIdToken,
    setUserProfile,
} from './services/authService'
import { getIdentities, getNotifications, getRequests } from './services/claimsService'
import { getCurrentUser } from './services/userService'
import { customThemeOptions, themeOptions } from './theme'
import { ApiDocs } from './views/ApiDocs'
import { Claim } from './views/Claim'
import Contracts from './views/Contracts/Contracts'
import { EarlyAccess } from './views/EarlyAccess'
import Persons from './views/Eligibility/Persons'
import Products from './views/Eligibility/Products'
import { SubmitEligibility } from './views/Eligibility/SubmitEligibility'
import Transactions from './views/Eligibility/Transactions'
import { ForbiddenNetwork } from './views/ForbiddenNetwork'
import { Home } from './views/Home'
import { Login } from './views/Login'
import { Lookup } from './views/Lookup'
import { Notifications } from './views/Notifications'
import { Requests } from './views/Requests'
import Settings from './views/Settings'
import SubmitDental from './views/SubmitDental/SubmitDental'
import { SubmitRaw } from './views/SubmitRaw'
import { UnAuthorized } from './views/UnAuthorized'

declare module '@mui/material/styles' {
    interface Theme {
        status: {
            danger: string
        }
    }
    // allow configuration using `createTheme`
    interface ThemeOptions {
        warning?: string
    }
}

export const ApplicationContext = createContext<IApplicationContext>({
    identities: [],
    notifications: [],
    requests: [],
    lastUpdate: 0,
    user: {},
    setLastUpdate: () => undefined,
})
const socket = new ReconnectingWebSocket(
    process.env.NODE_ENV === 'development'
        ? 'ws://localhost:3001'
        : `${window.location.protocol.startsWith('https') ? 'wss' : 'ws'}://${
              window.location.host
          }`,
)

function App() {
    useAnalytics()
    const [lastUpdate, setLastUpdate] = useState(0)
    const [requests, setRequests] = useState([])
    const [notifications, setNotifications] = useState([])
    const [identities, setIdentities] = useState<Identity[]>([])
    const [navigationVisible, setNavigationVisible] = useState(false)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isUnAuthorizedAccount, setUnAuthorizedAccount] = useState(false)
    const [isVerifiedUser, setIsVerifiedUser] = useState(false)
    const [user, setUser] = useState<any>()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [isLoading, setIsLoading] = useState(false)
    const [signer, setSigner] = useState<ethers.providers.JsonRpcSigner>()
    const theme = createTheme({ ...themeOptions, ...customThemeOptions })
    const navigate = useNavigate()
    const isReady = () => {
        return isLoggedIn() && user !== undefined
    }

    useEffect(() => {
        const token = getAccessToken()
        if (token) {
            getCurrentUser()
                .then(async (response: any) => {
                    setUser(response.data as User)
                    setUserProfile(response.data as User)
                    setIsVerifiedUser(response.data.isVerified)
                })
                .then(() => {
                    getIdentities()
                        .then(async (response) => {
                            setIdentities(await response.data)
                            socket.onmessage = () => {
                                setLastUpdate(new Date().getTime())
                            }
                        })
                        .catch(() => {
                            clearStorage()
                        })
                })
                .catch((error) => {
                    if (error.response.status === 403) {
                        const decodedToken: any = parseIdToken()
                        const user = {
                            name: decodedToken.name,
                            nickname: decodedToken.nickname,
                            user_id: decodedToken.oid ?? decodedToken.sub,
                            isVerified: false,
                        } as User
                        setUser(user)
                        setUserProfile(user)
                        setIsVerifiedUser(false)
                        navigate('/earlyaccess')
                    } else {
                        clearStorage()
                    }
                })
        }
    }, [getAccessToken()])

    useEffect(() => {
        if (Array.isArray(identities) && identities.length > 0) {
            const ownOrg = identities && identities.find((entry) => entry.self)
            if (!ownOrg) return
            Promise.all([getRequests(ownOrg.did), getNotifications()])
                .then(async ([requestsResponse, notificationsResponse]) => {
                    setRequests(await requestsResponse.data)
                    setNotifications(await notificationsResponse.data)
                })
                .catch(() => {
                    // TO DO
                })
        }
    }, [lastUpdate, identities])

    useEffect(() => {
        const configureSigner = async () => {
            const provider = new ethers.providers.Web3Provider((window as any).ethereum)
            await provider.send('eth_requestAccounts', [])
            const _signer = provider.getSigner()
            setSigner(_signer)
        }
        configureSigner()
    }, [(window as any).ethereum])

    const content = (
        <>
            <ApplicationContext.Provider
                value={{ identities, notifications, requests, lastUpdate, user, setLastUpdate }}
            >
                <ThemeProvider theme={theme}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <WalletContext.Provider value={{ signer }}>
                            <Box style={{ marginLeft: '250px' }}>
                                <Navigation />
                                <Header
                                    navigationVisible={navigationVisible}
                                    setNavigationVisible={setNavigationVisible}
                                />
                                <Routes>
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/submit/raw" element={<SubmitRaw />} />
                                    <Route path="/submit/dental" element={<SubmitDental />} />
                                    <Route
                                        path="/claims/:claimNumber/summary"
                                        element={<Claim view="summary" />}
                                    />
                                    <Route
                                        path="/claims/:claimNumber/details"
                                        element={<Claim view="details" />}
                                    />
                                    <Route
                                        path="/claims/:claimNumber/payments"
                                        element={<Claim view="payments" />}
                                    />
                                    <Route
                                        path="/claims/:claimNumber/services"
                                        element={<Claim view="services" />}
                                    />
                                    <Route
                                        path="/claims/:claimNumber/history"
                                        element={<Claim view="history" />}
                                    />
                                    <Route path="/lookup" element={<Lookup />} />
                                    <Route path="/requests" element={<Requests />} />
                                    <Route path="/notifications" element={<Notifications />} />
                                    <Route path="/" element={<Home />} />
                                    <Route path="/api/:endpointType" element={<ApiDocs />} />
                                    <Route path="/settings" element={<Settings />} />
                                    <Route path="/contracts" element={<Contracts />} />
                                    <Route path="/eligibility/members" element={<Persons />} />
                                    <Route path="/eligibility/patients" element={<Persons />} />
                                    <Route
                                        path="/eligibility/submit"
                                        element={<SubmitEligibility />}
                                    />
                                    <Route path="/eligibility/products" element={<Products />} />
                                    <Route
                                        path="/eligibility/transactions"
                                        element={<Transactions />}
                                    />
                                    <Route path="/earlyaccess" element={<EarlyAccess />} />
                                </Routes>
                            </Box>
                        </WalletContext.Provider>
                    </LocalizationProvider>
                </ThemeProvider>
            </ApplicationContext.Provider>
        </>
    )
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const forbiddenContent = <ForbiddenNetwork />
    const loginContent = <Login />
    const unauthorizedContent = <UnAuthorized />
    const earlyAccessContent = <EarlyAccess />
    const inProgress = (
        <Box
            style={{
                height: '100vh',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#F2F6F9',
            }}
        >
            <CircularProgress
                style={{ height: '100px', width: '100px' }}
                color="primary"
                size="lg"
                value={60}
            />
        </Box>
    )

    if (!isLoading && !isLoggedIn()) {
        return loginContent
    } else if (isLoading || !isReady()) {
        return inProgress
    } else if (isReady()) {
        if (!isVerifiedUser) {
            return earlyAccessContent
        }
        if (isUnAuthorizedAccount) {
            return unauthorizedContent
        } else {
            return content
        }
    }
    return inProgress
}

export default App
